<script>
/**
 * @Author: zhangxiaojian
 * @Date: 2025/6/25
 * @LastEditors: zhangxiaojian
 * @Description 知识问答正文内容
 **/
import MessageContent from './MessageContent.vue'
import QuestionTip from './QuestionTip.vue'
import { AIDify } from '@apis'
export default {
  name: 'Content',
  components: {
    MessageContent,
    QuestionTip
  },
  props: {
    // 对话列表
    messageList: {
      type: Array,
      default: () => []
    },
    // 知识类型 0--知识问答，1--运维助手
    knowledgeType: {
      type: Number,
      default: 0
    },
    // 最后一条消息的ID
    messageId: {
      type: String,
      default: ''
    },
    // 是否正在进行回答
    isAnswering: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    /**
     * 用户发送消息
     * @param {string} msg 消息内容
     */
    'sendMessage',
    /**
     * 重新加载答案
     * @param {object} item 对话项
     * @param {number} index 对话项索引
     */
    'reloadAnswer',
    /**
     * 清空会话
     */
    'clearConversation',
    /**
     * 停止回答
     */
    'stopAnswer'
  ],
  data() {
    return {
      // 用户输入的内容
      message: '',
      welcomWord: '' // 欢迎语
    }
  },
  provide() {
    return {
      // 设置滚动条状态
      scrollToBottom: this.scrollToBottom
    }
  },
  created() {
    this.getWelcomeWord()
  },
  methods: {
    /**
     * 获取欢迎语
     * @returns {Promise<void>}
     */
    async getWelcomeWord() {
      try {
        if (this.knowledgeType === 0) {
          let res = await AIDify.getWelcomeWord()
          if (res.data.status === 0) {
            // 获取成功
            let lineSpl = res.data.data.split('\n')
            this.welcomWord = `<div>${lineSpl[0]}</div><div>${lineSpl
              .slice(1)
              .join('\n')}</div>`
          } else {
            this.welcomWord = `<div>你好，我是你的AI助手</div><div>知识问答、知识搜索、与您一起体验AI魅力</div>`
          }
        } else if (this.knowledgeType === 1) {
          // 运维助手
          this.welcomWord = `<div>你好，我是你的智能运营助手 </div><div>遇到系统使用和业务难题，我在这里实时为您解答。</div>`
        }
      } catch (e) {
        this.welcomWord = `<div>你好，我是你的AI助手</div><div>知识问答、知识搜索、与您一起体验AI魅力</div>`
      }
    },
    /**
     * 发送消息
     */
    sendMessage() {
      // 正在回答时不允许发送消息
      if (this.isAnswering || !this.message.trim()) {
        if (!this.message.trim()) {
          this.message = ''
        }
        return
      }
      this.$emit('sendMessage', this.message)
      this.message = ''
    },
    /**
     * 重新加载答案
     * @param {object} item 对话项
     * @param {number} index 对话项索引
     */
    reloadAnswer(item, index) {
      this.$emit('reloadAnswer', item, index)
    },
    /**
     * 清空会话
     */
    clearConversation() {
      // 正在回答时不允许发送消息
      if (this.isAnswering) {
        return
      }
      this.$emit('clearConversation')
    },
    /**
     * 设置滚动条到底部
     */
    scrollToBottom() {
      const contentComponent = this.$refs.knowledgeContainer
      if (contentComponent) {
        contentComponent.scrollTo({
          top: contentComponent.scrollHeight,
          behavior: 'smooth'
        })
      }
    },
    /**
     * 停止回答
     */
    stopAnswer() {
      this.$emit('stopAnswer')
    },
    /**
     * 按提示提出新的问题
     * @param content {String} 问题内容
     */
    newQuestion(content) {
      this.message = content
      this.sendMessage()
    }
  }
}
</script>

<template>
  <div class="message-container">
    <div
      v-if="messageList.length === 0"
      class="message-container-nothing"
      v-html="welcomWord"
    ></div>
    <div v-else class="message-container-content" ref="knowledgeContainer">
      <!--      消息列表-->
      <message-content
        v-for="(item, index) in messageList"
        :is-user="item.isUser"
        :key="item.id"
        :show-reload="index === messageList.length - 1"
        :is-done="item.isDone"
        :content="item.content"
        :articles="item.articles"
        @reloadAnswer="() => reloadAnswer(item, index)"
      ></message-content>
      <!--      停止回答-->
      <div
        v-if="isAnswering"
        class="message-container-content-stop"
        @click="stopAnswer"
      >
        <span>
          <i class="el-icon-discover" />
          <span>停止生成</span>
        </span>
      </div>
      <QuestionTip
        v-else-if="messageId && knowledgeType === 0"
        :message-id="messageId"
        @newQuestion="newQuestion"
      />
    </div>
    <div class="message-container-enter">
      <div class="message-container-enter-input">
        <el-input
          type="textarea"
          :disabled="isAnswering"
          resize="none"
          placeholder="请输入您的问题吧，Enter键发送"
          @keyup.enter.native.prevent="sendMessage"
          v-model="message"
        ></el-input>
      </div>
      <div class="message-container-enter-operate">
        <span @click="clearConversation" v-if="this.knowledgeType === 0">
          <img
            class="new-session"
            src="../../../assets/img/knowledge/add.png"
          />
        </span>
        <span v-else></span>
        <span :class="`enter ${message ? 'focus' : ''}`" @click="sendMessage">
          <svg
            data-v-aae127c2=""
            width="17px"
            height="16px"
            viewBox="0 0 17 16"
            version="1.1"
            xml:space="preserve"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            class="chat-input-new-btn-send"
          >
            <g
              data-v-aae127c2=""
              id="页面-1"
              stroke="none"
              stroke-width="1"
              fill-rule="evenodd"
            >
              <g
                data-v-aae127c2=""
                id="魔方小窗icon"
                transform="translate(-247, -80)"
                fill-rule="nonzero"
              >
                <g data-v-aae127c2="" id="编组" transform="translate(247, 80)">
                  <path
                    data-v-aae127c2=""
                    d="M0.789453741,0.0736623487 C1.09174343,-0.0375717366 1.4261006,-0.0223213689 1.71700922,0.115969089 L16.0386413,6.9185747 C16.4562597,7.11687686 16.722407,7.53788236 16.722407,8.00019059 C16.722407,8.46249881 16.4562597,8.88350431 16.0386413,9.08180647 L1.7106233,15.8844121 C1.27082739,16.0930562 0.748491229,16.0146807 0.389297374,15.6861498 C0.0301035195,15.3576188 -0.0944463337,14.8443303 0.0742302533,14.3877111 L2.43343007,7.99939234 L0.0798179431,1.61147268 C-0.139523646,1.01655916 0.142943807,0.35379321 0.723998032,0.100004285 L0.789453741,0.0736623487 Z M12.9119332,8.7984311 L3.83992985,8.7984311 L1.91138077,14.0217179 L12.9119332,8.7984311 Z M1.91617022,1.97746596 L3.84072809,7.20195007 L12.9143279,7.20195007 L1.91617022,1.97746596 Z"
                    id="形状"
                  ></path>
                </g>
              </g>
            </g>
          </svg>
        </span>
      </div>
    </div>
    <div class="message-meta-tip">内容均由人工智能模型生成</div>
  </div>
</template>

<style scoped lang="scss">
.message-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  &-nothing {
    text-align: center;
    flex: 2;
    color: #7d8fb4;
    margin-top: 80px;
    ::v-deep {
      & > div:first-child {
        font-size: 24px;
      }
      & > div:last-child {
        font-size: 14px;
        margin-top: 20px;
      }
    }
  }
  &-content {
    flex: 2;
    overflow: auto;
    padding: 0 17px;
    margin-top: 30px;
    &-stop {
      text-align: center;
      & > span {
        font-size: 12px;
        color: #949db7;
        background: #fff;
        padding: 5px 10px;
        cursor: pointer;
        border-radius: 4px;
        & > span {
          margin-left: 5px;
        }
      }
    }
  }
  &-enter {
    background: #fff;
    border-radius: 8px;
    padding: 10px;
    height: 90px;
    margin: 0 17px;
    ::v-deep {
      .el-textarea.is-disabled .el-textarea__inner {
        background: none;
      }
      .el-textarea__inner {
        border: none;
      }
    }
    &-operate {
      display: flex;
      justify-content: space-between;
      padding: 0 10px;
      .new-session {
        width: 26px;
        cursor: pointer;
      }
      .enter {
        cursor: pointer;
        &.focus {
          svg {
            fill: #2943d6;
          }
        }
        svg {
          display: block;
          fill: #7d91b4;
          width: 17px;
          height: 16px;
        }
      }
    }
  }
  .message-meta-tip {
    font-size: 10px;
    color: #c8d1e2;
    text-align: center;
    margin-top: 10px;
    margin-bottom: -5px;
  }
}
</style>
